/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import sanitizeHtml from 'sanitize-html';

import Evolution from '../../components/Evolution/Evolution';
import LineChart from '../../components/LineChart/LineChart';
import BarChart from '../../components/BarChart/BarChart';
import StackedAreaChart from '../../components/StackedAreaChart/StackedAreaChart';
import OneLineChart from '../../components/OneLineChart/OneLineChart';
import Loader from '../../components/Loader/Loader';
import PdfPage from '../../components/PdfPage/PdfPage';
import Asterix, { AsterixType } from '../../components/Asterix/Asterix';
import UkBaseloadPricesTable from './UkBaseloadPricesTable/UkBaseloadPricesTable';
import UkPeakPricesTable from './UkPeakPricesTable/UkPeakPricesTable';
import NuclearOutagesTable from './NuclearOutagesTable/NuclearOutagesTable';
import SettlementTable from './SettlementTable/SettlementTable';

import { PowerModel } from '../../core/models/power';
import powerEvolution from '../../core/data/power/evolution';
import PowerService from '../../core/services/power-service';
import CommentsApiService from '../../core/services/comments-api-service';
import COLORS from '../../core/constants/colors';

import './PowerSection.scss';
import ChartHints from '../../components/ChartHints/ChartHints';

const powerService = new PowerService();
const commentsService = new CommentsApiService('');

export default function PowerSection(): JSX.Element {
  const [powerData, setPowerData] = useState<PowerModel>({} as PowerModel);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    powerService.getPower().then((response) => {
      setPowerData(response);
      setIsLoading(false);
    });
  }, []);

  const onSaveBase = (data: { value: string; id: number; type: string }) => {
    const sanitizeConf = {
      allowedTags: [],
      allowedAttributes: {},
    };
    const value = sanitizeHtml(data.value, sanitizeConf);
    if (data.id) {
      return commentsService.updateCustomPrice(data.id, data.type, value);
    }
    return commentsService.setCustomPrice(data.type, value).then((response: any) => {
      setPowerData({
        ...powerData,
        ukBasePricesTable: {
          ...powerData.ukBasePricesTable,
          pricesTable: powerData.ukBasePricesTable.pricesTable.map((item) => {
            if (item.postIndex === data.type) {
              return {
                ...item,
                idUpdate: response.id,
              };
            }
            return item;
          }),
        },
      });
    });
  };

  const onRemoveCustomPriceBase = (id: number) => {
    return commentsService.removeCustomPrice(id).then(() => {
      setPowerData({
        ...powerData,
        ukBasePricesTable: {
          ...powerData.ukBasePricesTable,
          pricesTable: powerData.ukBasePricesTable.pricesTable.map((item) => {
            if (item.idUpdate === id) {
              return {
                ...item,
                idUpdate: null,
                col4: Number(item.col4),
              };
            }
            return item;
          }),
        },
      });
    });
  };

  const onSavePeak = (data: { value: string; id: number; type: string }) => {
    const sanitizeConf = {
      allowedTags: [],
      allowedAttributes: {},
    };
    const value = sanitizeHtml(data.value, sanitizeConf);
    if (data.id) {
      return commentsService.updateCustomPrice(data.id, data.type, value);
    }
    return commentsService.setCustomPrice(data.type, value).then((response: any) => {
      setPowerData({
        ...powerData,
        ukPeakPricesTable: {
          ...powerData.ukPeakPricesTable,
          pricesTable: powerData.ukPeakPricesTable.pricesTable.map((item) => {
            if (item.postIndex === data.type) {
              return {
                ...item,
                idUpdate: response.id,
              };
            }
            return item;
          }),
        },
      });
    });
  };

  const onRemoveCustomPricePeak = (id: number) => {
    return commentsService.removeCustomPrice(id).then(() => {
      setPowerData({
        ...powerData,
        ukPeakPricesTable: {
          ...powerData.ukPeakPricesTable,
          pricesTable: powerData.ukPeakPricesTable.pricesTable.map((item) => {
            if (item.idUpdate === id) {
              return {
                ...item,
                idUpdate: null,
                col4: Number(item.col4),
              };
            }
            return item;
          }),
        },
      });
    });
  };

  return isLoading ? (
    <Loader />
  ) : (
    <div className="PowerSection" id="power">
      <div className="total-container">
        {/* <img src={powerIcon} alt="" /> */}
        <PdfPage>
          <h2>
            <span>Power</span>
          </h2>
          <div className="row">
            <div className="col-md-6 mb-8">
              <div className="section-summary mb-4">
                <Evolution
                  arrow={powerData.ukBase.arrow}
                  price={powerData.ukBase.value}
                  origin={powerEvolution.base.origin}
                  currency={powerEvolution.base.currency}
                />
                <Asterix type={AsterixType.basic} />
              </div>

              <LineChart
                title="UK Base"
                yAxisTitle="£ / MWh"
                colors={[COLORS.blue, COLORS.red, COLORS.orangeChart]}
                data={[...powerData.chartUkBase]}
              />
              <ChartHints />
            </div>
            <div className="col-md-6 mb-8">
              <div className="section-summary mb-4">
                <Evolution
                  arrow={powerData.ukPeak.arrow}
                  price={powerData.ukPeak.value}
                  origin={powerEvolution.peak.origin}
                  currency={powerEvolution.peak.currency}
                />
                <Asterix type={AsterixType.basic} />
              </div>

              <LineChart
                title="UK Peak"
                yAxisTitle="£ / MWh"
                colors={[COLORS.blue, COLORS.red, COLORS.orangeChart]}
                data={[...powerData.chartUkPeak]}
              />
              <ChartHints />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-8">
              <UkBaseloadPricesTable
                data={{ ...powerData.ukBasePricesTable }}
                onEditCell={onSaveBase}
                onRemoveCustomPrice={onRemoveCustomPriceBase}
              />
            </div>
            <div className="col-md-6 mb-8">
              <UkPeakPricesTable
                data={{ ...powerData.ukPeakPricesTable }}
                onEditCell={onSavePeak}
                onRemoveCustomPrice={onRemoveCustomPricePeak}
              />
            </div>
          </div>
        </PdfPage>

        <PdfPage>
          <div className="row mb-10">
            <div className="col-lg-12">
              <LineChart
                title="Power Day-Ahead Baseload"
                yAxisTitle="€/MWh"
                colors={[
                  COLORS.red,
                  COLORS.orange,
                  COLORS.purple,
                  COLORS.blue,
                  COLORS.green,
                  COLORS.aqua,
                  COLORS.gray,
                ]}
                data={[...powerData.chartUkPowerDayAhead]}
              />
              <ChartHints />
            </div>
          </div>

          <div className="row ">
            <div className="col-md-6 mb-8">
              <BarChart data={[...powerData.chartPowerGenerationMix]} />
            </div>

            <div className="col-md-6 mb-8">
              <StackedAreaChart
                title="UK Power Generation Mix 15 last days"
                yAxisTitle="GW"
                data={[...powerData.chartPowerGenerationMix15]}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-8">
              <LineChart
                title="Interconnector Power Flows"
                yAxisTitle="GW"
                colors={[COLORS.red, COLORS.orange, COLORS.purple, COLORS.blue, COLORS.green]}
                data={[...powerData.chartInterconnectorUK]}
              />
              <ChartHints />
            </div>
            <div className="col-md-6 mb-8">
              <LineChart
                title="Forecasted Wind&Solar Generation"
                yAxisTitle="MWh"
                colors={[COLORS.red, COLORS.blue]}
                data={[...powerData.chartForecasted]}
              />
              <ChartHints />
            </div>
          </div>
        </PdfPage>
        <PdfPage>
          <div className="row mb-8">
            <div className="col-lg-12">
              {powerData.nuclearUKTable ? (
                <NuclearOutagesTable data={[...powerData.nuclearUKTable]} />
              ) : null}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-8">
              <OneLineChart
                title="System Price"
                yAxisTitle="System Prices (£/MWh)"
                data={{ ...powerData.chartSystemPrice }}
              />
              <ChartHints />
            </div>
            <div className="col-md-6 mb-8">
              <OneLineChart
                title="Market Imbalance"
                yAxisTitle="Market Imbalance (MWh)"
                data={{ ...powerData.chartMarketImbalance }}
              />
              <ChartHints />
            </div>
          </div>

          <div className="row mb-8">
            <div className="col-lg-12">
              <SettlementTable data={[...powerData.powerSystemsTable]} />
            </div>
          </div>
        </PdfPage>
      </div>
    </div>
  );
}
